// Path: ./src/app/pages/PreferenceWatchItem/index.tsx
import React, { useState } from "react";
import { Box, Stack } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import { adminApiService } from "../../../utils/api";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { messages } from "../../../locales/messages";
import { toastSuccess } from "../../../utils/pageUtil/toast";
import { TableData } from "../../../utils/interface/tableData";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { SetupPageTopAppBar } from "../../../utils/commonStyledComponents/commonStyled";
import { BlackFontTransparentButton } from "../../../utils/commonStyledComponents/commonMuiStyled";
import {
  Container,
  DataNote,
  LeftButtonSection,
  NoteWrapper,
  TableWrapper,
} from "./styled";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { useLoading } from "../../../contextApi/LoadingContext";
import { useWatchItemTableColumns } from "./useWatchItemTableColumns";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { routes } from "../../../utils/constant/navigationRoutes";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";
import { WatchItemRow } from "../../../utils/preference/watchItemPayload";
import { SearchControl } from "../../components/FormKit/formControls";

const PreferenceWatchItemPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authHeaders = useAuthHeaders();

  const {
    paginationModel,
    sortModel,
    searchText,
    setQueryParams,
    handleTableChange,
  } = useQueryParams();
  const listGuard = useLatestRequestGuard();

  const [paginationLoading, setPaginationLoading] = useState(false);
  const [watchItemListData, setWatchItemListData] =
    useState<TableData<WatchItemRow>>();
  const [open, setOpen] = useState(false);
  const [watchItemToBeDeleted, setWatchItemToBeDeleted] =
    useState<WatchItemRow>();
  const { showLoadingScreen } = useLoading();

  const handleOpenDelete = (value: WatchItemRow) => {
    setWatchItemToBeDeleted(value);
    setOpen(true);
  };

  const columns = useWatchItemTableColumns({
    handleOnDelete: handleOpenDelete,
  });

  const handleCancel = () => {
    setOpen(false);
    setWatchItemToBeDeleted(undefined);
  };

  const fetchWatchItemList = async () => {
    const filter = searchText
      ? [`normalizedName||$contL||${searchText}`]
      : undefined;

    await guardedListFetch(
      listGuard,
      setPaginationLoading,
      () =>
        adminApiService.getManyBaseGfPreferenceWatchItemAdminControllerGfPreferenceWatchItem(
          undefined,
          undefined,
          filter,
          undefined,
          sortModel?.length
            ? [`${sortModel[0].field},${sortModel[0]?.sort?.toUpperCase()}`]
            : ["name,ASC"],
          undefined,
          paginationModel?.pageSize,
          0,
          paginationModel?.page,
          undefined,
          authHeaders,
        ),
      (watchItemList) =>
        setWatchItemListData((prev) => ({
          ...prev,
          rows: watchItemList.data as WatchItemRow[],
          total: watchItemList?.total,
        })),
    );
  };

  const handleDeleteWatchItem = async () => {
    if (!watchItemToBeDeleted?.id) return;

    showLoadingScreen(true);
    const result =
      await adminApiService.deleteOneBaseGfPreferenceWatchItemAdminControllerGfPreferenceWatchItem(
        watchItemToBeDeleted.id,
        authHeaders,
      );
    showLoadingScreen(false);

    if (result === undefined) return;

    handleCancel();
    toastSuccess(t(messages.Successfully_Deleted()));
    fetchWatchItemList();
  };

  React.useEffect(() => {
    setWatchItemListData((prev) => ({ ...prev, columns }));
  }, [t]);

  React.useEffect(() => {
    fetchWatchItemList();
  }, [searchText, paginationModel, sortModel]);

  return (
    <Container data-testid="preference-watch-item-page-test-id">
      <AccountCardWrapper title={t(messages.preference_watch_items())}>
        <Box data-testid="view-page">
          <SetupPageTopAppBar>
            <LeftButtonSection>
              <Stack direction="row" spacing={2} alignItems="center">
                <PermissionComponentWrapper
                  requiredPermission={["create"]}
                  requiredPermissionGroup={["tag"]}
                >
                  <BlackFontTransparentButton
                    variant="text"
                    startIcon={
                      <UploadFileIcon
                        style={{ color: "#ff8038", fontSize: 25 }}
                      />
                    }
                    onClick={() => {
                      navigate(routes.preferenceWatchItem.create);
                    }}
                    data-testid="create-button"
                  >
                    {t(messages.Create())}
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>

                <SearchControl
                  value={searchText}
                  placeholder={t(messages.preference_watch_item_search())}
                  onSearch={(value) =>
                    setQueryParams({ searchText: value, page: 1 })
                  }
                  dataTestId="preference-watch-item-search"
                />
              </Stack>
            </LeftButtonSection>
          </SetupPageTopAppBar>

          <NoteWrapper>
            <DataNote data-testid="preference-watch-item-intro-note">
              <InfoOutlinedIcon sx={{ fontSize: 15, mt: "1px" }} />
              <span>{t(messages.preference_watch_item_intro())}</span>
            </DataNote>
          </NoteWrapper>

          <TableWrapper>
            <ListTableQueryPagination
              loadFailed={listGuard.loadFailed}
              columns={watchItemListData?.columns}
              rows={watchItemListData?.rows}
              total={paginationLoading ? 0 : watchItemListData?.total}
              paginationModel={paginationModel}
              sortModel={sortModel}
              isLoading={paginationLoading}
              onTableChange={handleTableChange}
              dynamicHeightAdjuster={300}
            />
          </TableWrapper>

          <MuiConfirmationModal
            open={open}
            handleCancel={handleCancel}
            handleConfirm={handleDeleteWatchItem}
            title={`${t(messages.Delete_Confirmation())}`}
            description={`${t(
              messages.Are_you_sure_you_want_to_delete_These(),
            )} "${watchItemToBeDeleted?.name ?? ""}"? ${t(
              messages.This_action_cannot_be_undone(),
            )}.`}
          />
        </Box>
      </AccountCardWrapper>
    </Container>
  );
};

export default PreferenceWatchItemPage;
